'use client';

import React, { forwardRef, useId, useRef, useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { cn } from '../lib/utils';

export interface PasswordFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  inputClassName?: string;
  toggleClassName?: string;
  startAdornment?: React.ReactNode;
  showLabel?: string;
  hideLabel?: string;
}

function assignRef<T>(ref: React.ForwardedRef<T>, value: T | null) {
  if (typeof ref === 'function') ref(value);
  else if (ref) ref.current = value;
}

export const PasswordField = forwardRef<HTMLInputElement, PasswordFieldProps>(
  (
    {
      id,
      className,
      inputClassName,
      toggleClassName,
      startAdornment,
      showLabel = 'Show password',
      hideLabel = 'Hide password',
      ...props
    },
    forwardedRef,
  ) => {
    const generatedId = useId();
    const inputId = id ?? `password-${generatedId}`;
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [visible, setVisible] = useState(false);

    const toggleVisibility = () => {
      const input = inputRef.current;
      const selectionStart = input?.selectionStart ?? null;
      const selectionEnd = input?.selectionEnd ?? null;
      setVisible((current) => !current);
      requestAnimationFrame(() => {
        input?.focus({ preventScroll: true });
        if (selectionStart != null && selectionEnd != null) {
          try {
            input?.setSelectionRange(selectionStart, selectionEnd);
          } catch {
            // Some password managers do not expose a selection range.
          }
        }
      });
    };

    return (
      <div className={cn('relative', className)}>
        {startAdornment ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2"
          >
            {startAdornment}
          </span>
        ) : null}
        <input
          {...props}
          id={inputId}
          ref={(node) => {
            inputRef.current = node;
            assignRef(forwardedRef, node);
          }}
          type={visible ? 'text' : 'password'}
          className={cn(
            'flex h-11 w-full rounded-xl border border-slate-300 bg-white py-2 text-sm text-statura-ink placeholder:text-slate-500 focus:border-statura-green focus:outline-none focus:ring-[3px] focus:ring-statura-green/20 disabled:cursor-not-allowed disabled:opacity-60',
            startAdornment ? 'pl-10 pr-12' : 'px-4 pr-12',
            inputClassName,
          )}
        />
        <button
          type="button"
          aria-controls={inputId}
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          onClick={toggleVisibility}
          className={cn(
            'absolute right-0 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-r-xl text-slate-500 transition-colors hover:text-statura-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-statura-green',
            toggleClassName,
          )}
        >
          {visible ? (
            <EyeOff aria-hidden="true" className="h-4 w-4" />
          ) : (
            <Eye aria-hidden="true" className="h-4 w-4" />
          )}
        </button>
      </div>
    );
  },
);

PasswordField.displayName = 'PasswordField';
